import { I18N } from '@aurelia/i18n';
import { bindable, customElement, resolve } from 'aurelia';
import template from './import-protocol-detail-modal.html';
import type { ImportProtocolDetail, ImportProtocolEntry } from '../services/api.types';

@customElement({ name: 'import-protocol-detail-modal', template })
export class ImportProtocolDetailModal {
  private readonly i18n: I18N = resolve(I18N);

  @bindable public protocol!: ImportProtocolDetail;
  @bindable public closed!: () => void;
  @bindable public viewFiles!: (number: number) => void;

  public viewingError: ImportProtocolEntry | null = null;
  public copyFeedback = false;

  public close(): void {
    this.closed();
  }

  public statusKey(entry: ImportProtocolEntry): string {
    if (entry.isDuplicate) return 'importProtocols.entries.statusDuplicate';
    return entry.errorMessage ? 'importProtocols.entries.statusError' : 'importProtocols.entries.statusSuccess';
  }

  public statusClass(entry: ImportProtocolEntry): string {
    if (entry.isDuplicate) return 'duplicate';
    return entry.errorMessage ? 'error' : 'success';
  }

  public titleText(): string {
    return this.i18n.tr('importProtocols.detail.title', { number: this.protocol.number });
  }

  public sourceLabel(): string {
    const normalized = this.protocol.source.toLowerCase();
    return normalized === 'web' || normalized === 'mobile'
      ? this.i18n.tr(`importProtocols.sources.${normalized}`)
      : this.protocol.source;
  }

  public fileCountsText(): string {
    return this.i18n.tr('importProtocols.detail.fileCounts', {
      total: this.protocol.totalEntries,
      errors: this.protocol.errorEntries,
      duplicates: this.protocol.duplicateEntries,
    });
  }

  public openError(entry: ImportProtocolEntry): void {
    if (!entry.errorMessage) return;
    this.copyFeedback = false;
    this.viewingError = entry;
  }

  public closeError(): void {
    this.viewingError = null;
    this.copyFeedback = false;
  }

  public async copyError(): Promise<void> {
    const text = this.viewingError?.errorMessage;
    if (!text) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        this.legacyCopy(text);
      }
      this.copyFeedback = true;
      setTimeout(() => { this.copyFeedback = false; }, 2000);
    } catch {
      this.legacyCopy(text);
    }
  }

  private legacyCopy(text: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try { document.execCommand('copy'); } catch { /* best effort */ }
    document.body.removeChild(textarea);
  }
}
